import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Bar } from 'react-chartjs-2'
import { injectIntl } from 'react-intl'
import WidgetContainer from 'components/Apps/WidgetContainer'
import messages from 'components/Apps/SlaMonitor/translate'
import { getChartsRatio } from 'helpers/slaMonitor'
import { mathRound, getFormattedNum } from 'helpers/math'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import { SILO_CHART_COLORS } from 'constants/insight'
import CircleLoader from 'components/Loading/CircleLoader'
import { resetSiloDLFData } from 'reducers/slaMonitor/reducer'

class SiloDaysLeftFeeding extends Component {
  componentWillUnmount() {
    const { resetSiloDLFData } = this.props
    resetSiloDLFData()
  }

  getRandomColor = index => {
    const { length } = SILO_CHART_COLORS

    if (index > length) {
      // eslint-disable-next-line no-param-reassign
      index %= length
    }

    return SILO_CHART_COLORS[index]
  }

  getChartParams = () => {
    const labels = this.props.siloDLFData.get('dates')
    const cages = this.props.siloDLFData.get('cages')
    const data = this.props.siloDLFData.get('data')
    let datasets = []
    const yTicks = {
      autoSkipPadding: 60,
      max: 0,
    }
    const values = []

    for (const ci in cages) {
      if (Object.prototype.hasOwnProperty.call(cages, ci)) {
        const cage = cages[ci]
        const dsColor = this.getRandomColor(ci)
        const dsData = []

        for (const di in labels) {
          if (Object.prototype.hasOwnProperty.call(labels, di)) {
            const date = labels[di]
            const cageData = data[date].data[cage]
            const amount = Math.round(cageData.amount)
            dsData.push(amount)

            if (ci === '0') {
              values.push(amount)
            } else {
              values[di] += amount
            }
          }
        }

        datasets.push({
          label: cage,
          fill: false,
          backgroundColor: dsColor,
          borderColor: dsColor,
          pointRadius: 3,
          pointHitRadius: 3,
          pointHoverRadius: 3,
          data: dsData,
        })
      }
    }

    values.forEach(value => {
      if (value > yTicks.max) {
        yTicks.max = value
      }
    })

    yTicks.max = Math.ceil((yTicks.max + yTicks.max * 0.15) / 100) * 100
    datasets = datasets.reverse()

    return { labels, datasets, yTicks }
  }

  chartData = ({ labels, datasets }) => ({ labels, datasets })

  chartOptions = ({ yTicks }) => {
    const {
      siloDLFData,
      intl: { formatMessage, formatNumber },
    } = this.props

    return {
      aspectRatio: getChartsRatio(),
      responsive: true,
      legend: {
        display: true,
        reverse: true,
        onClick: evt => evt.stopPropagation(),
      },
      scales: {
        xAxes: [
          {
            stacked: true,
            scaleLabel: {
              display: true,
              labelString: `${formatMessage(messages.days)}`,
            },
            ticks: {
              beginAtZero: true,
            },
          },
        ],
        yAxes: [
          {
            stacked: true,
            ticks: yTicks,
            scaleLabel: {
              display: true,
              labelString: `${formatMessage(messages.feedUsed)} (kg)`,
            },
          },
        ],
      },
      tooltips: {
        enabled: true,
        mode: 'single',
        intersect: true,
        backgroundColor: '#03254a',
        animationDuration: 300,
        titleFontSize: 13,
        bodyFontSize: 10,
        caretSize: 10,
        displayColors: false,
        callbacks: {
          title: item => `${item[0].label}`,
          label: ({ index }, { datasets }) => {
            let feedUsed = 0
            const rows = [
              feedUsed,
              `${formatMessage(messages.dayLeftFeeding)}: ${siloDLFData.get(
                'days_left',
              )}`,
            ]

            for (let key = datasets.length - 1; key >= 0; key -= 1) {
              const dataset = datasets[key]
              const value = dataset.data[index]
              rows.push(
                `${formatMessage(messages.cage)} (${
                  dataset.label
                }): ${formatNumber(mathRound(value))} kg`,
              )
              feedUsed += value
            }

            rows.splice(
              0,
              1,
              `${formatMessage(messages.feedUsed)}: ${formatNumber(
                mathRound(feedUsed),
              )} kg`,
            )

            return rows
          },
        },
      },
      plugins: {
        datalabels: {
          display: true,
          anchor: 'end',
          align: 'end',
          color: 'black',
          font: {
            weight: 'bold',
            size: ifWindowWidthLessThan(1280) ? 9 : 11,
          },
          formatter: (value, { dataIndex, datasetIndex, chart }) => {
            const { datasets } = chart.config.data
            const lastDatasetIndex = datasets.length - 1

            if (lastDatasetIndex !== datasetIndex) {
              return ''
            }

            if (lastDatasetIndex === 0) {
              return `${formatNumber(value)}`
            }

            // eslint-disable-next-line no-param-reassign
            value = 0

            for (const key in datasets) {
              if (Object.prototype.hasOwnProperty.call(datasets, key)) {
                // eslint-disable-next-line no-param-reassign
                value += datasets[key].data[dataIndex]
              }
            }

            // eslint-disable-next-line no-param-reassign
            value = Math.ceil(value)

            return value ? `${formatNumber(value)}` : ''
          },
        },
      },
    }
  }

  getSideContent = () => {
    const {
      intl: { formatMessage },
      siteLevel,
      goBack,
      siloDLFData,
    } = this.props

    if (ifWindowWidthLessThan(992) && siteLevel !== true) {
      return (
        <button type="button" className="btn-link" onClick={goBack}>
          {formatMessage(messages.back)}
        </button>
      )
    }

    return siloDLFData.get('last_updated')
  }

  render() {
    const { siloDLFData, chartLoader } = this.props
    const {
      intl: { formatMessage },
    } = this.props
    const chartParams = this.getChartParams()

    return (
      <WidgetContainer
        name={{
          start: formatMessage(messages.daysLeftFeeding),
          end: siloDLFData.get('silo_title'),
        }}
        sideTitle={this.getSideContent()}
        className="feed-module">
        <div className="feed-chart-box">
          <div className="chart-info">
            <div className="info-item">
              <span className="item-count">
                {getFormattedNum(mathRound(siloDLFData.get('remaining_feed')))}
              </span>
              <span className="item-name">
                {formatMessage(messages.remaining)} (kg)
              </span>
            </div>
            <div className="info-item">
              <span className="item-count">
                {getFormattedNum(
                  mathRound(siloDLFData.get('avg_daily_feeding')),
                )}
              </span>
              <span className="item-name">
                {formatMessage(messages.avgDaily)} (kg)
              </span>
            </div>
            <div className="info-item">
              <span className="item-count">{siloDLFData.get('days_left')}</span>
              <span className="item-name">
                {formatMessage(messages.daysLeft)}
              </span>
            </div>
          </div>
          <div className="chart-content">
            <CircleLoader loading={chartLoader} />
            <Bar
              width={null}
              height={null}
              data={this.chartData(chartParams)}
              options={this.chartOptions(chartParams)}
            />
          </div>
        </div>
      </WidgetContainer>
    )
  }
}

export default connect(
  state => ({
    siloDLFData: state.getIn(['slaMonitor', 'siloDLFData']),
    chartLoader: state.getIn(['slaMonitor', 'siloDLFLoader']),
  }),
  {
    resetSiloDLFData,
  },
)(injectIntl(SiloDaysLeftFeeding))
